import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage?: string;
}

// Class component because React only supports error boundaries via
// componentDidCatch/getDerivedStateFromError, not hooks.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: `${error.name}: ${error.message}` };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in app render tree:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          gap={2}
          padding={2}
          textAlign="center"
        >
          <Typography variant="h6">Something went wrong.</Typography>
          <Typography variant="body2" color="text.secondary">
            Please reload the page. If this keeps happening, try opening this link in your browser instead of an in-app browser.
          </Typography>
          {this.state.errorMessage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-word', maxWidth: 400 }}
            >
              {this.state.errorMessage}
            </Typography>
          )}
          <Button variant="contained" sx={{ backgroundColor: '#22438C' }} onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
